// Servicio de API para conectar con el backend MySQL
const API_BASE_URL = process.env.REACT_APP_API_URL || "/api";

// Interfaz para las respuestas de la API
interface ApiResponse<T> {
  data?: T;
  error?: string;
  message?: string;
  code?: string;
  fields?: Record<string, string>;
}

export type PlatformSubscriptionStatus = "active" | "paused" | "cancelled";
export type PlatformSubscriptionDisplayStatus =
  | "active"
  | "paused"
  | "cancelled"
  | "scheduled"
  | "expired";

export interface PlatformUserStats {
  invoiceCount: number;
  clientCount: number;
  expenseCount: number;
  lastLoginAt?: string | null;
}

export interface PlatformUserAiUsage {
  tokensTotal30d: number;
  tokensTotalAll: number;
  requestsCount: number;
}

export interface PlatformUsersSummary {
  totalUsers: number;
  active: number;
  paused: number;
  cancelled: number;
  aiTokensMonth: number;
}

export interface PlatformUserRow {
  id: string;
  name: string;
  email: string;
  role: string;
  isActive: boolean;
  onboardingCompleted?: boolean;
  companyId: string;
  companyName: string;
  createdAt?: string;
  subscriptionStatus?: PlatformSubscriptionStatus;
  subscriptionDisplayStatus?: PlatformSubscriptionDisplayStatus;
  accessStartsAt?: string | null;
  accessEndsAt?: string | null;
  pausedAt?: string | null;
  cancelledAt?: string | null;
  stats?: PlatformUserStats;
  ai?: PlatformUserAiUsage;
}

export interface PlatformInvite {
  url: string;
  expiresAt: string;
  expiresInDays?: number;
}

export interface InviteValidation {
  valid: boolean;
  user: { email: string; name: string; onboardingCompleted?: boolean };
  company: { id: string; name: string; cif: string };
}

export interface OnboardingCompleteResult {
  message: string;
  token: string;
  user: { id: string; username: string; email: string; role: string; companyId: string; companyName: string };
  company: { id: string; name: string; cif: string };
}

export interface KitDigitalClient {
  id: string;
  companyId: string;
  clientName: string;
  url: string;
  amountTotal: number;
  pago70: number;
  pago30: number;
  feeTalentosoft: number;
  netInvoice70: number;
  paid70At: string | null;
  paid30At: string | null;
  facturaKd70: boolean;
  facturaKd30: boolean;
  facturaKd70InvoiceId: string | null;
  facturaKd30InvoiceId: string | null;
  ordenPago: number | null;
  notes: string;
  splitWithDris?: boolean;
  partnerName?: string;
}

export interface KitDigitalStats {
  totalKits: number;
  cobrado70: number;
  pendiente70: number;
  cobrado30: number;
  pendiente30: number;
  countPending70: number;
  countPending30: number;
  countSplitWithDris?: number;
}

// Clase para manejar las llamadas a la API
class ApiService {
  private token: string | null = null;

  // Configurar token de autenticación
  setToken(token: string) {
    this.token = token;
    localStorage.setItem("authToken", token);
  }

  // Obtener token almacenado
  getToken(): string | null {
    if (!this.token) {
      this.token = localStorage.getItem("authToken");
    }
    return this.token;
  }

  // Limpiar token
  clearToken() {
    this.token = null;
    localStorage.removeItem("authToken");
  }

  // Headers de autorización
  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };

    const token = this.getToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    return headers;
  }

  // Método genérico para hacer requests
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    try {
      const url = `${API_BASE_URL}${endpoint}`;
      // Preparar headers; si es FormData no forzar Content-Type
      let headers: HeadersInit = this.getHeaders();
      const isFormData = options && (options as any).body && typeof (options as any).body.append === 'function';
      if (isFormData) {
        const hObj: any = { ...headers };
        delete hObj['Content-Type'];
        headers = hObj;
      }
      const response = await fetch(url, {
        ...options,
        headers,
      });

      const contentType = response.headers.get("content-type") || "";
      let data: Record<string, unknown> = {};
      if (contentType.includes("application/json")) {
        data = await response.json();
      } else {
        const text = await response.text();
        if (!response.ok) {
          return {
            error: response.status >= 500 ? "Error del servidor" : "Error en la petición",
            message:
              response.status === 502 || response.status === 503
                ? "El backend no está disponible. Inténtelo de nuevo en unos minutos."
                : text.slice(0, 200) || `HTTP ${response.status}`,
          };
        }
        return { error: "Respuesta no válida", message: "El servidor no devolvió JSON" };
      }

      if (!response.ok) {
        const apiError = (data.error as string) || "Error en la petición";
        const apiMessage = (data.message as string) || apiError;
        const errPayload =
          data && typeof data === "object" && (data.ok === false || data.code || data.canMarkConnected)
            ? (data as T)
            : undefined;
        return {
          error: apiError,
          message: apiMessage,
          code: data.code as string | undefined,
          fields: data.fields as Record<string, string> | undefined,
          ...(errPayload ? { data: errPayload } : {}),
        };
      }

      return { data: data as T };
    } catch (error) {
      console.error("Error en API request:", error);
      return {
        error: "Error de conexión",
        message: "No se pudo conectar con el servidor",
      };
    }
  }

  // ===== AUTENTICACIÓN =====

  // Login: una sola petición (email o username) para no disparar rate-limit ni bloqueos
  async login(identifier: string, password: string) {
    const trimmed = identifier.trim();
    const body = trimmed.includes("@")
      ? { email: trimmed, password }
      : { username: trimmed, password };

    const res = await this.request("/auth/login", {
      method: "POST",
      body: JSON.stringify(body),
    });

    if (res.data && !(res as any).error) {
      return res;
    }

    return {
      error: res.error || "Credenciales inválidas",
      message: res.message || "Usuario o contraseña incorrectos",
      code: res.code,
    };
  }

  // Registro de usuario
  async register(userData: {
    username: string;
    email: string;
    password: string;
    companyName: string;
    companyCIF: string;
  }) {
    return this.request("/auth/register", {
      method: "POST",
      body: JSON.stringify(userData),
    });
  }

  // Obtener perfil del usuario
  async getProfile() {
    return this.request("/auth/profile");
  }

  // ===== USUARIOS =====

  // Obtener usuarios
  async getUsers() {
    return this.request("/users");
  }

  // Obtener usuario específico
  async getUser(id: string) {
    return this.request(`/users/${id}`);
  }

  // Crear usuario
  async createUser(userData: any) {
    return this.request("/users", {
      method: "POST",
      body: JSON.stringify(userData),
    });
  }

  // Actualizar usuario
  async updateUser(id: string, userData: any) {
    return this.request(`/users/${id}`, {
      method: "PUT",
      body: JSON.stringify(userData),
    });
  }

  // ===== USUARIOS PLATAFORMA (solo superadmin) =====

  async getPlatformUsers() {
    return this.request<{ users: PlatformUserRow[]; summary?: PlatformUsersSummary }>("/admin/users");
  }

  async getPlatformUserUsage(userId: string) {
    return this.request<{
      userId: string;
      companyId: string;
      usage: {
        tokensTotal30d: number;
        tokensTotalAll: number;
        requestsCount: number;
        byModel: { model: string; requestsCount: number; tokensTotal: number }[];
        recent: { tokensPrompt: number; tokensCompletion: number; model: string | null; createdAt: string }[];
      };
    }>(`/admin/users/${userId}/usage`);
  }

  async createPlatformUser(payload: { email: string; name: string; companyName?: string }) {
    return this.request<{ user: PlatformUserRow; invite: PlatformInvite }>("/admin/users", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  async regeneratePlatformInvite(userId: string) {
    return this.request<{ invite: PlatformInvite }>(`/admin/users/${userId}/regenerate-invite`, {
      method: "POST",
    });
  }

  async updatePlatformUser(id: string, payload: { isActive?: boolean; name?: string }) {
    return this.request(`/admin/users/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  }

  async deletePlatformUser(id: string) {
    return this.request(`/admin/users/${id}`, { method: "DELETE" });
  }

  async setPlatformUserPassword(
    userId: string,
    payload: { password: string; confirmPassword: string }
  ) {
    return this.request<{ message: string }>(`/admin/users/${userId}/password`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  }

  async updatePlatformSubscription(
    userId: string,
    payload: {
      status?: PlatformSubscriptionStatus;
      accessStartsAt?: string | null;
      accessEndsAt?: string | null;
    }
  ) {
    return this.request<{ user: PlatformUserRow }>(`/admin/users/${userId}/subscription`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  }

  async pausePlatformSubscription(userId: string) {
    return this.request<{ user: PlatformUserRow }>(`/admin/users/${userId}/subscription/pause`, {
      method: "POST",
    });
  }

  async cancelPlatformSubscription(userId: string) {
    return this.request<{ user: PlatformUserRow }>(`/admin/users/${userId}/subscription/cancel`, {
      method: "POST",
    });
  }

  // ===== ONBOARDING =====

  async validateInviteToken(token: string) {
    return this.request<InviteValidation>(`/onboarding/invite/${encodeURIComponent(token)}`);
  }

  async completeOnboarding(
    token: string,
    payload: {
      password: string;
      companyName: string;
      companyCif: string;
      activity?: string;
      defaultIva?: number;
      defaultIrpf?: number;
      assistantAnswers?: unknown;
      enableWooCommerce?: boolean;
    }
  ) {
    return this.request<OnboardingCompleteResult>(
      `/onboarding/invite/${encodeURIComponent(token)}/complete`,
      { method: "POST", body: JSON.stringify(payload) }
    );
  }

  async onboardingAssistant(token: string, message: string, context?: Record<string, unknown>) {
    return this.request(
      `/onboarding/invite/${encodeURIComponent(token)}/assistant`,
      { method: "POST", body: JSON.stringify({ message, context }) }
    );
  }

  // Eliminar usuario
  async deleteUser(id: string) {
    return this.request(`/users/${id}`, {
      method: "DELETE",
    });
  }

  // ===== EMPRESAS =====

  // Obtener empresas del usuario
  async getCompanies() {
    return this.request("/companies");
  }

  // Obtener empresa específica
  async getCompany(id: string) {
    return this.request(`/companies/${id}`);
  }

  // Crear empresa
  async createCompany(companyData: any) {
    return this.request("/companies", {
      method: "POST",
      body: JSON.stringify(companyData),
    });
  }

  // Actualizar empresa
  async updateCompany(id: string, companyData: any) {
    return this.request(`/companies/${id}`, {
      method: "PUT",
      body: JSON.stringify(companyData),
    });
  }

  // Eliminar empresa
  async deleteCompany(id: string) {
    return this.request(`/companies/${id}`, {
      method: "DELETE",
    });
  }

  // Subir logo de empresa
  async uploadCompanyLogo(id: string, file: File) {
    const form = new FormData();
    form.append('logo', file);
    return this.request(`/companies/${id}/logo`, {
      method: 'POST',
      body: form
    });
  }

  // Activar empresa
  async activateCompany(id: string) {
    return this.request(`/companies/${id}/activate`, {
      method: "POST",
    });
  }

  // ===== CLIENTES =====

  // Obtener clientes
  async getClients() {
    return this.request("/clients");
  }

  // Obtener cliente específico
  async getClient(id: string) {
    return this.request(`/clients/${id}`);
  }

  // Crear cliente
  async createClient(clientData: any) {
    return this.request("/clients", {
      method: "POST",
      body: JSON.stringify(clientData),
    });
  }

  // Actualizar cliente
  async updateClient(id: string, clientData: any) {
    return this.request(`/clients/${id}`, {
      method: "PUT",
      body: JSON.stringify(clientData),
    });
  }

  // Eliminar cliente
  async deleteClient(id: string) {
    return this.request(`/clients/${id}`, {
      method: "DELETE",
    });
  }

  // Buscar clientes
  async searchClients(term: string) {
    return this.request(`/clients/search/${term}`);
  }

  // ===== FACTURAS =====

  // Obtener facturas
  async getInvoices(params?: {
    status?: string;
    clientId?: string;
    startDate?: string;
    endDate?: string;
    projectId?: string;
    group?: string;
    groupIds?: string;
    page?: number;
    limit?: number;
  }) {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString());
        }
      });
    }

    const endpoint = params ? `/invoices?${queryParams}` : "/invoices";
    return this.request(endpoint);
  }

  // Obtener factura específica
  async getInvoice(id: string) {
    return this.request(`/invoices/${id}`);
  }

  // Crear factura
  async createInvoice(invoiceData: any) {
    return this.request("/invoices", {
      method: "POST",
      body: JSON.stringify(invoiceData),
    });
  }

  // Actualizar factura
  async updateInvoice(id: string, invoiceData: any) {
    return this.request(`/invoices/${id}`, {
      method: "PUT",
      body: JSON.stringify(invoiceData),
    });
  }

  async updateInvoiceStatus(id: string, status: 'draft' | 'sent' | 'paid' | 'overdue') {
    return this.request(`/invoices/${id}/status`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    });
  }

  async bulkUpdateInvoiceStatus(ids: string[], status: 'draft' | 'sent' | 'paid' | 'overdue') {
    return this.request<{
      updated: string[];
      failed: Array<{ id: string; error: string; message: string }>;
      message: string;
    }>("/invoices/bulk-update-status", {
      method: "POST",
      body: JSON.stringify({ ids, status }),
    });
  }

  // Eliminar factura
  async deleteInvoice(id: string) {
    return this.request(`/invoices/${id}`, {
      method: "DELETE",
    });
  }

  // Eliminar varias facturas
  async bulkDeleteInvoices(ids: string[]) {
    return this.request<{
      deleted: string[];
      failed: Array<{ id: string; error: string; message: string }>;
      message: string;
    }>("/invoices/bulk-delete", {
      method: "POST",
      body: JSON.stringify({ ids }),
    });
  }

  async bulkAssignProject(ids: string[], projectId: string | null) {
    return this.request<{
      updated: string[];
      failed: Array<{ id: string; error: string }>;
      message: string;
    }>("/invoices/bulk-assign-project", {
      method: "POST",
      body: JSON.stringify({ ids, projectId }),
    });
  }

  async bulkAssignGroups(ids: string[], groupIds: string[]) {
    return this.request<{
      updated: string[];
      failed: Array<{ id: string; error: string; message?: string }>;
      message: string;
    }>("/invoices/bulk-assign-groups", {
      method: "POST",
      body: JSON.stringify({ ids, groupIds }),
    });
  }

  // Obtener siguiente número de factura
  async getNextInvoiceNumber() {
    return this.request("/invoices/next-number");
  }

  // Importación de documentos con IA (NVIDIA Llama vía backend)
  async previewDocumentImport(formData: FormData) {
    return this.request<{ results: unknown[]; count: number }>(
      "/invoices/import-document/preview",
      { method: "POST", body: formData }
    );
  }

  async importDocumentInvoices(formData: FormData) {
    return this.request<{ results: unknown[]; imported: number; count: number }>(
      "/invoices/import-document",
      { method: "POST", body: formData }
    );
  }

  async confirmDocumentImport(payload: { invoices: { file: string; extracted: unknown }[] }) {
    return this.request<{
      results: Array<{
        success: boolean;
        file?: string;
        invoiceId?: string | null;
        invoiceNumber?: string;
        errors?: string[];
      }>;
      imported: number;
      message?: string;
    }>("/invoices/import-document/confirm", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  /** @deprecated Use previewDocumentImport */
  async previewPdfImport(formData: FormData) {
    return this.previewDocumentImport(formData);
  }

  /** @deprecated Use importDocumentInvoices */
  async importPdfInvoices(formData: FormData) {
    return this.importDocumentInvoices(formData);
  }

  /** @deprecated Use confirmDocumentImport */
  async confirmPdfImport(payload: { invoices: { file: string; extracted: unknown }[] }) {
    return this.confirmDocumentImport(payload);
  }

  // ===== FACTURACIÓN AUTOMÁTICA =====

  // Obtener configuraciones
  async getAutoBillingConfigs() {
    return this.request("/auto-billing");
  }

  // Obtener configuración específica
  async getAutoBillingConfig(id: string) {
    return this.request(`/auto-billing/${id}`);
  }

  // Crear configuración
  async createAutoBillingConfig(configData: any) {
    return this.request("/auto-billing", {
      method: "POST",
      body: JSON.stringify(configData),
    });
  }

  // Actualizar configuración
  async updateAutoBillingConfig(id: string, configData: any) {
    return this.request(`/auto-billing/${id}`, {
      method: "PUT",
      body: JSON.stringify(configData),
    });
  }

  // Eliminar configuración
  async deleteAutoBillingConfig(id: string) {
    return this.request(`/auto-billing/${id}`, {
      method: "DELETE",
    });
  }

  // Generar factura automática
  async generateAutoInvoice(id: string) {
    return this.request(`/auto-billing/${id}/generate-invoice`, {
      method: "POST",
    });
  }

  // ===== EMAIL / PDF FACTURAS =====

  private async fetchInvoicePdfBlob(
    invoiceId: string,
    mode: "preview" | "download"
  ): Promise<{ blob?: Blob; filename?: string; error?: string; message?: string }> {
    try {
      const path =
        mode === "download"
          ? `/invoices/${invoiceId}/pdf`
          : `/invoices/${invoiceId}/pdf/preview`;
      const url = `${API_BASE_URL}${path}`;
      const token = this.getToken();
      const headers: HeadersInit = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const response = await fetch(url, { headers });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return {
          error: (err as { error?: string }).error || "Error generando PDF",
          message:
            (err as { message?: string }).message ||
            (mode === "download"
              ? "No se pudo descargar el PDF"
              : "No se pudo generar la vista previa del PDF"),
        };
      }
      const blob = await response.blob();
      const contentType = response.headers.get("Content-Type") || blob.type || "";
      if (!blob.size || contentType.includes("json") || contentType.includes("text/html")) {
        return {
          error: "PDF inválido",
          message: "El servidor no devolvió un PDF válido",
        };
      }
      const disposition = response.headers.get("Content-Disposition") || "";
      const match = disposition.match(/filename="?([^";]+)"?/i);
      const filename = match?.[1];
      const pdfBlob =
        blob.type === "application/pdf"
          ? blob
          : new Blob([await blob.arrayBuffer()], { type: "application/pdf" });
      return { blob: pdfBlob, filename };
    } catch {
      return {
        error: "Error de red",
        message:
          mode === "download"
            ? "No se pudo descargar el PDF. Comprueba tu conexión."
            : "No se pudo cargar la vista previa del PDF. Comprueba tu conexión.",
      };
    }
  }

  async fetchInvoicePdf(
    invoiceId: string
  ): Promise<{ blob?: Blob; error?: string; message?: string }> {
    return this.fetchInvoicePdfBlob(invoiceId, "preview");
  }

  async downloadInvoicePdf(
    invoiceId: string,
    suggestedFilename?: string
  ): Promise<{ ok: boolean; error?: string; message?: string }> {
    const result = await this.fetchInvoicePdfBlob(invoiceId, "download");
    if (result.error || !result.blob) {
      return { ok: false, error: result.error, message: result.message };
    }
    const url = URL.createObjectURL(result.blob);
    const link = document.createElement("a");
    link.href = url;
    link.download =
      result.filename ||
      suggestedFilename ||
      `factura-${invoiceId}.pdf`;
    link.click();
    URL.revokeObjectURL(url);
    return { ok: true };
  }

  async sendInvoiceEmail(
    invoiceId: string,
    payload?: { to?: string; subject?: string; body?: string }
  ) {
    return this.request<{ message?: string; to?: string; id?: string }>(
      `/invoices/${invoiceId}/send-email`,
      {
        method: "POST",
        body: JSON.stringify(payload || {}),
      }
    );
  }

  // Probar Gmail SMTP
  async testGmailSMTP(params: { user: string; appPassword: string; toEmail?: string; fromName?: string; }) {
    return this.request("/email/test", {
      method: "POST",
      body: JSON.stringify(params),
    });
  }


  // ===== DASHBOARD / BACKUP / RECIBIDAS =====

  async getDashboardStats(projectId?: string) {
    const qs = projectId ? `?projectId=${encodeURIComponent(projectId)}` : "";
    return this.request(`/dashboard/stats${qs}`);
  }

  async getBackupJobs() {
    return this.request("/backup/jobs");
  }

  async createBackupJob(payload: { label: string; scheduleCron?: string }) {
    return this.request("/backup/jobs", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  async getReceivedInvoices() {
    return this.request("/received-invoices");
  }

  async uploadReceivedInvoice(file: File) {
    const form = new FormData();
    form.append("file", file);
    return this.request("/received-invoices/upload", { method: "POST", body: form });
  }

  // ===== INTEGRACIÓN RESERVAS ENTIK =====

  async getReservasPending() {
    return this.request("/integrations/reservas/pending");
  }

  async getReservasDashboard() {
    return this.request("/integrations/reservas/dashboard");
  }

  async syncReservas() {
    return this.request("/integrations/reservas/sync", { method: "POST" });
  }

  async createInvoiceFromReservation(bookingId: number) {
    return this.request(`/integrations/reservas/${bookingId}/invoice`, {
      method: "POST",
      body: "{}",
    });
  }


  // ===== WOOCOMMERCE =====

  async getWooCommerceSettings() {
    return this.request("/integrations/woocommerce/settings");
  }

  async patchWooCommerceSettings(enabled: boolean, autoInvoice?: boolean) {
    const body: { enabled?: boolean; autoInvoice?: boolean } = {};
    if (enabled !== undefined) body.enabled = enabled;
    if (autoInvoice !== undefined) body.autoInvoice = autoInvoice;
    return this.request("/integrations/woocommerce/settings", {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  }

  async testWooCommerceConnection(creds: {
    storeUrl?: string;
    consumerKey?: string;
    consumerSecret?: string;
  }) {
    return this.request("/integrations/woocommerce/test", {
      method: "POST",
      body: JSON.stringify(creds),
    });
  }

  async connectWooCommerce(payload: {
    storeUrl?: string;
    consumerKey?: string;
    consumerSecret?: string;
    useDemo?: boolean;
  }) {
    return this.request("/integrations/woocommerce/connect", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  async disconnectWooCommerce() {
    return this.request("/integrations/woocommerce/disconnect", {
      method: "POST",
      body: "{}",
    });
  }

  async getWooCommerceOrders() {
    return this.request("/integrations/woocommerce/orders");
  }

  async syncWooCommerceOrders(createInvoices = false) {
    return this.request("/integrations/woocommerce/sync", {
      method: "POST",
      body: JSON.stringify({ createInvoices }),
    });
  }


  async getWooCommercePluginInfo() {
    return this.request("/integrations/woocommerce/plugin-info");
  }

  async getWooCommerceOAuthStatus(state: string) {
    return this.request(`/integrations/woocommerce/oauth/status?state=${encodeURIComponent(state)}`);
  }

  async approveWooCommerceOAuth(state: string) {
    return this.request("/integrations/woocommerce/oauth/approve", {
      method: "POST",
      body: JSON.stringify({ state }),
    });
  }

  async verifyWooCommercePlugin() {
    return this.request("/integrations/woocommerce/oauth/verify", {
      method: "POST",
      body: "{}",
    });
  }

  async markWooCommerceConnected(storeUrl?: string) {
    return this.request("/integrations/woocommerce/oauth/mark-connected", {
      method: "POST",
      body: JSON.stringify(storeUrl ? { storeUrl } : {}),
    });
  }

  async installWooCommercePlugin(payload: { storeUrl: string; wpUser: string; wpAppPassword: string }) {
    return this.request("/integrations/woocommerce/install-plugin", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  getWooCommerceAuthorizeUrl(storeUrl: string) {
    const base = (process.env.REACT_APP_API_URL || "/api").replace(/\/+$/, "");
    const token = localStorage.getItem("authToken") || "";
    const qs = new URLSearchParams({ store: storeUrl });
    if (token) qs.set("token", token);
    return `${base}/integrations/woocommerce/oauth/authorize?${qs.toString()}`;
  }

  async createInvoiceFromWooCommerceOrder(orderId: number) {
    return this.request(`/integrations/woocommerce/create-invoice/${orderId}`, {
      method: "POST",
      body: "{}",
    });
  }

  // ===== GOOGLE DRIVE =====

  async getGoogleDriveConfig() {
    return this.request("/integrations/google-drive/config");
  }

  async getGoogleDriveStatus() {
    return this.request("/integrations/google-drive/status");
  }

  async getGoogleDriveAuthUrl() {
    return this.request("/integrations/google-drive/auth-url");
  }

  async disconnectGoogleDrive() {
    return this.request("/integrations/google-drive/disconnect", {
      method: "POST",
      body: "{}",
    });
  }

  async saveGoogleDriveConfig(clientId: string, clientSecret?: string) {
    return this.request("/integrations/google-drive/config", {
      method: "POST",
      body: JSON.stringify({ clientId, clientSecret: clientSecret || undefined }),
    });
  }

  // ===== GMAIL =====

  async getGmailStatus() {
    return this.request("/integrations/gmail/status");
  }

  async getGmailAuthUrl() {
    return this.request("/integrations/gmail/auth-url");
  }

  async disconnectGmail() {
    return this.request("/integrations/gmail/disconnect", { method: "POST", body: "{}" });
  }

  async getGmailMessages(q?: string) {
    const qs = q ? `?q=${encodeURIComponent(q)}` : "";
    return this.request(`/integrations/gmail/messages${qs}`);
  }

  async getGmailMessage(id: string) {
    return this.request(`/integrations/gmail/messages/${id}`);
  }

  async sendGmailMessage(payload: { to: string; subject: string; body: string }) {
    return this.request("/integrations/gmail/send", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  // ===== IA (NVIDIA) =====

  async getAiStatus() {
    return this.request<{
      configured: boolean;
      ok?: boolean;
      provider: "nvidia";
      model: string;
      modelDisplayName?: string;
      latencyMs?: number | null;
      keySource: "env" | "database" | null;
      keyInEnv: boolean;
      message?: string;
      usage?: {
        requestsToday: number;
        requestsDate: string;
        lastTokens: number | null;
        lastPromptTokens: number | null;
        lastCompletionTokens: number | null;
        lastTotalTokens: number | null;
        lastRequestAt: string | null;
      };
    }>("/integrations/ai/status");
  }

  // ===== PROYECTOS =====

  async getProjects() {
    return this.request<{ projects: Array<{
      id: string;
      company_id: string;
      name: string;
      slug: string;
      color?: string;
      brand?: string;
    }> }>("/projects");
  }

  async saveAiConfig(payload: { model: string }) {
    return this.request("/integrations/ai/config", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  async aiChat(messages: { role: string; content: string }[], maxTokens?: number) {
    return this.request<{
      choices?: { message?: { content?: string } }[];
    }>("/integrations/ai/chat", {
      method: "POST",
      body: JSON.stringify({ messages, max_tokens: maxTokens }),
    });
  }


  async getAssistantStats(projectId?: string) {
    const q = projectId ? `?projectId=${encodeURIComponent(projectId)}` : "";
    return this.request<{ success?: boolean; stats?: Record<string, unknown> }>(
      `/assistant/stats${q}`
    );
  }

  async assistantChat(
    message: string,
    context?: {
      selectedExpenseIds?: string[];
      projectId?: string;
      activeTab?: string;
    }
  ) {
    return this.request<{
      success?: boolean;
      message?: string;
      reply?: string;
      invoiceId?: string;
      summary?: Record<string, unknown>;
      intent?: string;
      navigateTo?: string | null;
    }>("/assistant/chat", {
      method: "POST",
      body: JSON.stringify({ message, context }),
    });
  }

  // ===== GASTOS DEL TRABAJO =====
  async getProfessionalExpenses(params?: {
    trimestre?: number;
    ejercicio?: number;
    categoria?: string;
    estado?: string;
    expenseKind?: string;
    includeLinked?: boolean;
  }) {
    const q = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null) q.append(k, String(v));
      });
    }
    const qs = q.toString();
    return this.request(`/professional-expenses${qs ? `?${qs}` : ""}`);
  }

  async getProfessionalExpensesQuarterly(year: number, quarter: number) {
    return this.request(`/professional-expenses/quarterly/${year}/${quarter}`);
  }

  async getExpenseQuarterlySummary(year: number, quarter: number) {
    return this.getProfessionalExpensesQuarterly(year, quarter);
  }

  async getUnlinkedExpenses(kind?: string) {
    const endpoint = kind
      ? `/professional-expenses/unlinked?kind=${kind}`
      : "/professional-expenses/unlinked";
    return this.request<{ expenses: import("../types").ProfessionalExpense[] }>(endpoint);
  }

  async extractExpenseTicket(file: File, expenseKind?: string) {
    const form = new FormData();
    form.append("file", file);
    if (expenseKind) form.append("expenseKind", expenseKind);
    return this.request<{
      suggestions: import("../types").ExpenseExtractSuggestions;
      ocrJson?: Record<string, unknown>;
      rawText?: string;
    }>("/professional-expenses/extract", { method: "POST", body: form });
  }

  async createProfessionalExpense(formData: FormData) {
    return this.request<{ expense: import("../types").ProfessionalExpense }>(
      "/professional-expenses",
      { method: "POST", body: formData }
    );
  }

  async updateProfessionalExpense(id: string, formData: FormData) {
    return this.request(`/professional-expenses/${id}`, { method: "PUT", body: formData });
  }

  async deleteProfessionalExpense(id: string) {
    return this.request(`/professional-expenses/${id}`, { method: "DELETE" });
  }

  async triggerProfessionalExpenseOcr(id: string) {
    return this.request(`/professional-expenses/${id}/ocr`, { method: "POST", body: "{}" });
  }



  // ===== VERI*FACTU / AEAT =====
  async getAeatHealth() {
    return this.request("/aeat/health");
  }

  async getAeatAccountProfile() {
    return this.request("/aeat/account");
  }

  async saveAeatAccountProfile(payload: Record<string, unknown>) {
    return this.request("/aeat/account", { method: "PUT", body: JSON.stringify(payload) });
  }

  async getVerifactuHealth() {
    return this.request("/verifactu/health");
  }

  async getVerifactuChain() {
    return this.request("/verifactu/chain");
  }

  async getVerifactuRegistro(invoiceId: string) {
    return this.request(`/verifactu/invoice/${invoiceId}/registro`);
  }

  async createVerifactuRegistro(invoiceId: string) {
    return this.request(`/verifactu/invoice/${invoiceId}/registro`, {
      method: "POST",
      body: JSON.stringify({}),
    });
  }

  async remitVerifactuRegistro(invoiceId: string) {
    return this.request(`/verifactu/invoice/${invoiceId}/remitir`, {
      method: "POST",
      body: JSON.stringify({}),
    });
  }

  async getVerifactuConfig() {
    return this.request("/verifactu/config");
  }

  async prepareAeatInvoice(invoiceId: string) {
    return this.request(`/aeat/invoice/${invoiceId}/prepare`, {
      method: "POST",
      body: JSON.stringify({}),
    });
  }

  async submitAeatInvoice(invoiceId: string) {
    return this.request(`/aeat/invoice/${invoiceId}/submit`, {
      method: "POST",
      body: JSON.stringify({}),
    });
  }
  async getPaymentMethods() {
    return this.request<{
      paymentMethods: import("../types").PaymentMethodConfig[];
      sourceDefaults: Record<string, string>;
    }>("/settings/payment-methods");
  }

  async createPaymentMethod(data: Partial<import("../types").PaymentMethodConfig>) {
    return this.request("/settings/payment-methods", { method: "POST", body: JSON.stringify(data) });
  }

  async updatePaymentMethod(id: string, data: Partial<import("../types").PaymentMethodConfig>) {
    return this.request(`/settings/payment-methods/${id}`, { method: "PATCH", body: JSON.stringify(data) });
  }

  async deletePaymentMethod(id: string) {
    return this.request(`/settings/payment-methods/${id}`, { method: "DELETE" });
  }

  async updateSourcePaymentDefaults(sourceDefaults: Record<string, string>) {
    return this.request("/settings/payment-defaults-by-source", {
      method: "PATCH",
      body: JSON.stringify({ sourceDefaults }),
    });
  }

  async getBusinessLines() {
    return this.request<{ businessLines: import("../types").BusinessLine[] }>("/settings/business-lines");
  }

  async createBusinessLine(data: Partial<import("../types").BusinessLine>) {
    return this.request("/settings/business-lines", { method: "POST", body: JSON.stringify(data) });
  }

  async updateBusinessLine(id: string, data: Partial<import("../types").BusinessLine>) {
    return this.request(`/settings/business-lines/${id}`, { method: "PATCH", body: JSON.stringify(data) });
  }

  async getInvoiceGroups() {
    return this.request<{ groups: import("../types").InvoiceGroup[] }>("/invoice-groups");
  }

  async createInvoiceGroup(data: { name: string; slug?: string; color?: string; sortOrder?: number }) {
    return this.request<{ group: import("../types").InvoiceGroup }>("/invoice-groups", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateInvoiceGroup(id: string, data: Partial<import("../types").InvoiceGroup> & { isActive?: boolean }) {
    return this.request<{ group: import("../types").InvoiceGroup }>(`/invoice-groups/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  async deleteInvoiceGroup(id: string) {
    return this.request(`/invoice-groups/${id}`, { method: "DELETE" });
  }

  async getFiscalQuarterlySummary(year: number, quarter: number) {
    return this.request(`/fiscal/quarterly-summary?year=${year}&quarter=${quarter}`);
  }

  // ===== KIT DIGITAL =====

  async getKitDigitalClients() {
    return this.request<{ clients: KitDigitalClient[]; stats: KitDigitalStats }>(
      "/kit-digital/clients"
    );
  }

  async createKitDigitalClient(payload: Partial<KitDigitalClient>) {
    return this.request<{ client: KitDigitalClient }>("/kit-digital/clients", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  async patchKitDigitalClient(id: string, payload: Partial<KitDigitalClient>) {
    return this.request<{ client: KitDigitalClient }>(`/kit-digital/clients/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  }

  async deleteKitDigitalClient(id: string) {
    return this.request(`/kit-digital/clients/${id}`, { method: "DELETE" });
  }

  async markKitDigitalPaid70(id: string) {
    return this.request<{ client: KitDigitalClient }>(
      `/kit-digital/clients/${id}/mark-paid-70`,
      { method: "POST", body: "{}" }
    );
  }

  async markKitDigitalPaid30(id: string) {
    return this.request<{ client: KitDigitalClient }>(
      `/kit-digital/clients/${id}/mark-paid-30`,
      { method: "POST", body: "{}" }
    );
  }

  async unmarkKitDigitalPaid70(id: string) {
    return this.request<{ client: KitDigitalClient }>(
      `/kit-digital/clients/${id}/mark-unpaid-70`,
      { method: "POST", body: "{}" }
    );
  }

  async unmarkKitDigitalPaid30(id: string) {
    return this.request<{ client: KitDigitalClient }>(
      `/kit-digital/clients/${id}/mark-unpaid-30`,
      { method: "POST", body: "{}" }
    );
  }

  async generateKitDigitalInvoice70(id: string) {
    return this.request<{ invoiceId: string; invoiceNumber: string; duplicate?: boolean }>(
      `/kit-digital/clients/${id}/generate-invoice-70`,
      { method: "POST", body: "{}" }
    );
  }

  async generateKitDigitalInvoice30(id: string) {
    return this.request<{ invoiceId: string; invoiceNumber: string; duplicate?: boolean }>(
      `/kit-digital/clients/${id}/generate-invoice-30`,
      { method: "POST", body: "{}" }
    );
  }

  async linkKitDigitalInvoice70(id: string, invoiceId: string) {
    return this.request<{ client: KitDigitalClient }>(
      `/kit-digital/clients/${id}/link-invoice-70`,
      { method: "POST", body: JSON.stringify({ invoiceId }) }
    );
  }

  async linkKitDigitalInvoice30(id: string, invoiceId: string) {
    return this.request<{ client: KitDigitalClient }>(
      `/kit-digital/clients/${id}/link-invoice-30`,
      { method: "POST", body: JSON.stringify({ invoiceId }) }
    );
  }

  // ===== UTILIDADES =====

  // Verificar si el usuario está autenticado
  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  // Logout
  logout() {
    this.clearToken();
    // Redirigir a la raíz (no hay ruta /login en esta app)
    window.location.href = "/";
  }

  // Change password
  async changePassword(userId: string, currentPassword: string, newPassword: string) {
    return this.request("/auth/change-password", {
      method: "POST",
      body: JSON.stringify({ userId, currentPassword, newPassword }),
    });
  }
}

// Instancia única del servicio
export const apiService = new ApiService();

// Exportar tipos para uso en otros archivos
export type { ApiResponse };
